import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  useTheme as useMuiTheme,
  Backdrop,
  Alert,
  alpha,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  CalendarToday as CalendarIcon,
  Info as InfoIcon,
  DirectionsCar as VehicleIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import XLSX from 'sheetjs-style';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import axiosInstance from "../utlis/axiosInstance";
import moment from 'moment';
import { useTheme } from "../utlis/useTheme";
import DeviceSearchSelect, { DeviceOption } from "../components/DeviceSearchSelect";

interface ReportData {
  deviceId: string;
  deviceName: string;
  type: string;
  eventTime: string;
  geofenceId?: string;
  geofence?: {
    name: string;
  };
  attributes?: {
    commandType?: string;
    command?: string;
    operator?: string;
    maintenance?: string;
  };
  alarm?: {
    name?: string;
    speed?: number;
    latitude?: number;
    longitude?: number;
    device_name?: string;
    speed_limit?: number;
    terminal_id?: number;
  };
}

interface Device {
  id: string;
  name: string;
}

const eventTypes = [
  { value: 'allEvents', label: 'All Events' },
  { value: 'geofence_exit', label: 'Geofence Exit' },
  { value: 'geofence_enter', label: 'Geofence Enter' },
  { value: 'ignition_on', label: 'Ignition ON' },
  { value: 'ignition_off', label: 'Ignition OFF' },
  { value: 'speed_limit', label: 'Speed Limit Exceeded' },
  { value: 'low_battery', label: 'Low Battery' },
  { value: 'device_offline', label: 'Offline' },
  { value: 'maintenance_due', label: 'Maintenance Due' },
  { value: 'engine_cut', label: 'engine_cut' },
  { value: 'fuel_theft', label: 'fuel_theft' },
  { value: 'tampering', label: 'tampering' },
  { value: 'overspeed', label: 'speed_limit' },
  { value: 'idle', label: 'idle' },
  { value: 'moving', label: 'moving' },
  { value: 'stopped', label: 'stopped' },
  { value: 'vibration_alarm', label: 'vibration_alarm' },
  { value: 'sos_alarm', label: 'sos_alarm' },
  { value: 'charging_complete', label: 'charging_complete' },
  { value: 'charging_connected', label: 'charging_connected' },
  { value: 'charging_disconnected', label: 'charging_disconnected' },
  { value: 'battery_power', label: 'battery_power' },
  { value: 'commandResult', label: 'Command Result' },
  { value: 'deviceOnline', label: 'Status Online' },
  { value: 'deviceUnknown', label: 'Status Unknown' },
  { value: 'deviceOffline', label: 'Status Offline' },
  { value: 'deviceInactive', label: 'Device Inactive' },
  { value: 'queuedCommandSent', label: 'Queued Command Sent' },
  { value: 'deviceMoving', label: 'Device Moving' },
  { value: 'deviceStopped', label: 'Device Stopped' },
  { value: 'deviceOverspeed', label: 'Speed Limit Exceeded' },
  { value: 'deviceFuelDrop', label: 'Fuel Drop' },
  { value: 'deviceFuelIncrease', label: 'Fuel Increase' },
  { value: 'alarm', label: 'Alarm' },
  { value: 'maintenance', label: 'Maintenance Required' },
  { value: 'textMessage', label: 'Text Message Required' },
  { value: 'driverChanged', label: 'Driver Changed' },
  { value: 'media', label: 'Media' }
];

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return format(date, 'dd:MM:yyyy');
};

const formatType = (type: string) => {
  if (!type) return '';

  const typeMap: { [key: string]: string } = {
    "allEvents": "All Events",
    "geofence_exit": "Geofence Exit",
    "geofence_enter": "Geofence Enter",
    "ignition_on": "Ignition ON",
    "ignition_off": "Ignition OFF",
    "speed_limit": "Speed Limit Exceeded",
    "low_battery": "Low Battery",
    "device_offline": "Offline",
    "maintenance_due": "Maintenance Due",
    "engine_cut": "engine_cut",
    "fuel_theft": "fuel_theft",
    "tampering": "tampering",
    "overspeed": "speed_limit",
    "idle": "idle",
    "moving": "moving",
    "stopped": "stopped",
    "vibration_alarm": "vibration_alarm",
    "sos_alarm": "sos_alarm",
    "charging_complete": "charging_complete",
    "charging_connected": "charging_connected",
    "charging_disconnected": "charging_disconnected",
    "battery_power": "battery_power",
    "commandResult": "Command Result",
    "deviceOnline": "Status Online",
    "deviceUnknown": "Status Unknown",
    "deviceOffline": "Status Offline",
    "deviceInactive": "Device Inactive",
    "queuedCommandSent": "Queued Command Sent",
    "deviceMoving": "Device Moving",
    "deviceStopped": "Device Stopped",
    "deviceOverspeed": "Speed Limit Exceeded",
    "deviceFuelDrop": "Fuel Drop",
    "deviceFuelIncrease": "Fuel Increase",
    "geofenceExit": "Geofence Exit",
    "geofenceEnter": "Geofence Enter",
    "alarm": "Alarm",
    "ignitionOn": "Ignition ON",
    "ignitionOff": "Ignition OFF",
    "maintenance": "Maintenance Required",
    "textMessage": "Text Message Required",
    "driverChanged": "Driver Changed",
    "media": "Media"
  };

  return typeMap[type] || type
    .replace(/([A-Z0-9])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, (str) => str.toLowerCase())
    .trim();
};

const getStatusChipClass = (type: string) => {
  if (type.includes('Online')) return 'success';
  if (type.includes('Offline')) return 'error';
  if (type.includes('alarm')) return 'warning';
  return 'default';
};

const EventReport: React.FC = () => {
  const { getPrimaryColor, getPrimaryLightColor, getPrimaryDarkColor } = useTheme();
  const muiTheme = useMuiTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [allFetchedEvents, setAllFetchedEvents] = useState<any[]>([]); // Store all fetched events for filtering
  const [selectedDevices, setSelectedDevices] = useState<DeviceOption[]>(
    id ? [{ id, name: id }] : []
  );
  const [selectedEventType, setSelectedEventType] = useState<string>('allEvents');
  const [fromDate, setFromDate] = useState<Date>(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0); // Today 12:00 AM
    return date;
  });
  const [toDate, setToDate] = useState<Date>(new Date());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [reportFetched, setReportFetched] = useState(false);
  const [isInitialMount, setIsInitialMount] = useState(true);

  // Fetch devices on component mount
  // useEffect(() => {
  //   fetchDevices();
  // }, []);

  // const fetchDevices = async () => {
  //   try {
  //     const response = await axiosInstance.get("/gpstracker/devicehandler/?limit=1000&offset=0");

  //     if (response.data.status && response.data.responseData) {
  //       setDevices(response.data.responseData);
  //     } else {
  //       setError("Failed to fetch devices. Please try again.");
  //     }
  //   } catch (error) {
  //     console.error("Error fetching devices:", error);
  //     setError("Error fetching devices. Please check your connection.");
  //   }
  // };

  const handleGenerateReport = async () => {
    if (!selectedDevices.length) {
      setError("Please select at least one device.");
      return;
    }

    const selectedDeviceIds = selectedDevices.map((device) => device.id);
    const selectedDeviceMap = Object.fromEntries(
      selectedDevices.map((device) => [device.id, device.name || device.id])
    );

    setLoading(true);
    setError(null);
    setIsInitialMount(false); // Mark that we're no longer in initial mount

    try {
      const fromDateStr = moment(fromDate)
        .subtract(5, "hours")
        .subtract(30, "minutes")
        .format("YYYY-MM-DDTHH:mm:ss[Z]");

      const toDateStr = moment(toDate)
        .subtract(5, "hours")
        .subtract(30, "minutes")
        .format("YYYY-MM-DDTHH:mm:ss[Z]");

      // Fetch data for each selected device and combine results
      const allEventsPromises = selectedDeviceIds.map(async (deviceId) => {
        const queryParams = new URLSearchParams({
          from: fromDateStr,
          to: toDateStr,
          limit: '10000',
          device_id: deviceId
        });

        // Add event_type filter if not 'allEvents'
        if (selectedEventType && selectedEventType !== 'allEvents') {
          queryParams.append('event_type', selectedEventType);
        }

        const response = await axiosInstance.get(`/gpstracker/eventhandler/?${queryParams.toString()}`);
        return response.data?.status ? (response.data?.responseData?.events || []) : [];
      });

      const allEventsArrays = await Promise.all(allEventsPromises);
      const allEvents = allEventsArrays.flat();

      // Store all fetched events
      setAllFetchedEvents(allEvents);

      const transformedData = allEvents.map((event: any) => ({
        deviceId: event.device?.toString(),
      deviceName:
        event.alarm?.device_name ||
        selectedDeviceMap[event.device?.toString()] ||
        `Device ${event.device}`,
        type: event.event_type,
        eventTime: event.server_time,
        geofenceId: event.geofence?.toString(),
        geofence: event.alarm?.geofence_id ? {
          name: `Geofence ${event.alarm.geofence_id}`
        } : undefined,
        attributes: {
          commandType: event.attributes?.commandType,
          command: event.attributes?.command,
          operator: event.attributes?.operator,
          maintenance: event.maintenance
        },
        alarm: event.alarm
      }));

      setReportData(transformedData || []);
      setPage(0);
    } catch (error) {
      console.error("Error fetching report:", error);
      setError("Error generating report. Please try again.");
      setReportData([]);
    } finally {
      setLoading(false);
      setReportFetched(true);
    }
  };

  const handleExportExcel = async () => {
    if (reportData.length === 0) return;

    setDownloading(true);
    setDownloadProgress(0);

    try {
      // Create header rows like your CSV format
      const headerRows = [
        ["From DateTime", "", moment(fromDate).format('DD:MM:YYYY'), "", "", "", "", "", "", ""],
        ["To DateTime", "", moment(toDate).format('DD:MM:YYYY'), "", "", "", "", "", "", ""],
         ["Vehicle No", "", selectedDevices.map(device => device.name || device.id).join(', '), "", "", "", "", "", "", ""],
        ["", "", "", "", "", "", "", "", "", ""], // Empty row
        ["Sr.", "Vehicle Number", "Type", "Date & Time", "Alarm Name", "Speed", "Latitude", "Longitude", "Device Name", "Speed Limit", "Terminal ID"]
      ];

      // Create data rows
      const dataRows = reportData.map((row, index) => [
        index + 1,
        row.deviceName || "",
        formatType(row.type) || "",
        formatDate(row.eventTime),
        formatType(row.type) || "-",
        row.alarm?.speed || "-",
        row.alarm?.latitude || "-",
        row.alarm?.longitude || "-",
        row.alarm?.device_name || "-",
        row.alarm?.speed_limit || "-",
        row.alarm?.terminal_id || "-"
      ]);

      // Combine header and data rows
      const allRows = [...headerRows, ...dataRows];

      setDownloadProgress(50);

      const worksheet = XLSX.utils.aoa_to_sheet(allRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Event Report");

      setDownloadProgress(75);

      // Generate Excel file
      XLSX.writeFile(workbook, `Event_Report_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`);

      setDownloadProgress(100);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setError('Failed to export report. Please try again.');
    } finally {
      setTimeout(() => {
        setDownloading(false);
        setDownloadProgress(0);
      }, 1000);
    }
  };

  const handleExportPDF = async () => {
    if (reportData.length === 0) return;

    setDownloading(true);
    setDownloadProgress(0);

    try {
      const doc = new jsPDF('landscape');

      doc.setFontSize(14);
      doc.text('Event Report', 14, 16);
      doc.setFontSize(10);
      doc.text(`From: ${moment(fromDate).format('DD:MM:YYYY')}`, 14, 24);
      doc.text(`To: ${moment(toDate).format('DD:MM:YYYY')}`, 110, 24);
       doc.text(`Devices: ${selectedDevices.map(device => device.name || device.id).join(', ')}`, 14, 30);

      const columns = [
        'Vehicle Number',
        'Type',
        'Date & Time',
        'Alarm Name',
        'Speed',
        'Latitude',
        'Longitude',
        'Device Name',
        'Speed Limit',
        'Terminal ID'
      ];

      const rows = reportData.map(row => [
        row.deviceName || '',
        formatType(row.type) || '',
        formatDate(row.eventTime),
        formatType(row.type) || '-',
        row.alarm?.speed || '-',
        row.alarm?.latitude || '-',
        row.alarm?.longitude || '-',
        row.alarm?.device_name || '-',
        row.alarm?.speed_limit || '-',
        row.alarm?.terminal_id || '-'
      ]);

      setDownloadProgress(30);

      // Use autotable plugin
      // @ts-ignore
      (autoTable as any)(doc, {
        head: [columns],
        body: rows,
        startY: 36,
        styles: { fontSize: 7 },
        headStyles: { fillColor: [230, 236, 255] },
        theme: 'grid',
        didDrawPage: (dataArg: any) => {
          // page footer
          const pageSize = doc.internal.pageSize;
          const pageHeight = pageSize.height ? pageSize.height : (pageSize as any).getHeight();
          doc.setFontSize(9);
          const pageNumber = doc.getNumberOfPages();
          doc.text(`Page ${pageNumber}`, doc.internal.pageSize.getWidth() - 30, pageHeight - 10);
        }
      });

      setDownloadProgress(80);

      doc.save(`Event_Report_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.pdf`);

      setDownloadProgress(100);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      setError('Failed to export PDF. Please try again.');
    } finally {
      setTimeout(() => {
        setDownloading(false);
        setDownloadProgress(0);
      }, 800);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = reportData.slice(startIndex, endIndex);
  const totalPages = Math.ceil(reportData.length / rowsPerPage);

  return (
    <Box
      sx={{
        fontFamily: '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        backgroundColor: alpha(getPrimaryColor() || "#1D2B56", 0.02),
        minHeight: '100vh',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box
          sx={{
            padding: '1.5rem 1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}
        >
          <Button
            onClick={() => navigate('/reports')}
            sx={{
              color: '#6b7280',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '2.5rem',
              height: '2.5rem',
              borderRadius: '0.5rem',
              minWidth: 'auto',
              '&:hover': {
                color: '#374151',
                backgroundColor: '#f3f4f6',
              },
            }}
          >
            <ArrowBackIcon sx={{ width: '1.5rem', height: '1.5rem' }} />
          </Button>
          <Typography
            variant="h5"
            sx={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#1f2937',
              margin: 0,
            }}
          >
            Event Report
          </Typography>
        </Box>
      </Box>

      <Box sx={{ padding: '1.5rem' }}>
        {/* Error Alert */}
        {error && (
          <Box sx={{ margin: '1rem 0' }}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Box>
        )}
        {/* Filter Panel */}
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${alpha(getPrimaryColor() || "#1D2B56", 0.2)}`,
            padding: '1rem',
            marginBottom: '1.5rem',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexWrap: 'wrap',
              justifyContent: 'flex-start',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: {
                  xs: 60,
                  lg: 120,
                },
                maxWidth: {
                  xs: '100%',
                  lg: 170,
                },
                flex: '1 1 140px',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: {
                    sm: '0.7rem',
                    lg: '0.75rem',
                  },
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '0.25rem',
                }}
              >
                From Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  value={fromDate}
                  onChange={(newValue: Date | null) => newValue && setFromDate(newValue)}
                  maxDateTime={toDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: {
                        '& input': {
                          fontSize: {
                            xs: '0.625rem',
                            sm: '0.8rem',
                            lg: '1rem',
                          },
                        },
                      },
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: {
                  xs: 60,
                  lg: 120,
                },
                maxWidth: {
                  xs: '100%',
                  lg: 170,
                },
                flex: '1 1 140px',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: {
                    sm: '0.7rem',
                    lg: '0.75rem',
                  },
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '0.25rem',
                }}
              >
                To Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  value={toDate}
                  onChange={(newValue: Date | null) => newValue && setToDate(newValue)}
                  minDateTime={fromDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: {
                        '& input': {
                          fontSize: {
                            xs: '0.625rem',
                            sm: '0.8rem',
                            lg: '1rem',
                          },
                        },
                      },
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: {
                  xs: 200,
                  lg: 200,
                },
                maxWidth: {
                  xs: '100%',
                  lg: 300,
                },
                flex: '0 0 auto',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: {
                    sm: '0.7rem',
                    lg: '0.75rem',
                  },
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '0.25rem',
                }}
              >
                Select Devices
              </Typography>
              <DeviceSearchSelect
                label="Add Device"
                multiple
                value={selectedDevices}
                onChange={(newValue) =>
                  setSelectedDevices((newValue as DeviceOption[]) || [])
                }
                maxSelection={10}
                onMaxSelectionReached={() =>
                  setError("Maximum 10 devices can be selected.")
                }
              />
              {/* Selected Devices Summary */}
              {selectedDevices.length > 0 && (
                <Box sx={{ marginTop: '0.5rem' }}>
                  {selectedDevices.length > 50 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#e3f2fd',
                        border: '1px solid #90caf9',
                        borderRadius: '8px',
                        padding: '0.5rem 0.75rem',
                        gap: '0.5rem',
                      }}
                    >
                      <Typography sx={{ color: '#0284F5', fontSize: '0.75rem', fontWeight: 500 }}>
                        {`${selectedDevices.length.toLocaleString()} Devices Selected`}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => setSelectedDevices([])}
                        sx={{
                          minWidth: 'auto',
                          padding: '0.25rem 0.5rem',
                          color: '#0284F5',
                          fontSize: '0.7rem',
                          textTransform: 'none',
                          border: '1px solid #0284F5',
                          '&:hover': {
                            backgroundColor: 'rgba(2, 132, 245, 0.1)',
                          },
                        }}
                        variant="outlined"
                      >
                        Clear All
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {selectedDevices.map((device) => {
                        return (
                          <Box
                            key={device.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              backgroundColor: '#e3f2fd',
                              border: '1px solid #90caf9',
                              borderRadius: '16px',
                              padding: '0.25rem 0.5rem',
                              gap: '0.25rem',
                            }}
                          >
                            <Typography sx={{ color: '#0284F5', fontSize: '0.75rem' }}>
                              {device.name || device.id}
                            </Typography>
                            <Button
                              size="small"
                              onClick={() =>
                                setSelectedDevices((prev) =>
                                  prev.filter((selected) => selected.id !== device.id)
                                )
                              }
                              sx={{
                                minWidth: 'auto',
                                padding: '0',
                                color: '#0284F5',
                                '&:hover': {
                                  backgroundColor: 'rgba(2, 132, 245, 0.1)',
                                },
                              }}
                            >
                              ×
                            </Button>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: {
                  xs: 60,
                  lg: 120,
                },
                maxWidth: {
                  xs: '100%',
                  lg: 170,
                },
                flex: '1 1 140px',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: {
                    sm: '0.7rem',
                    lg: '0.75rem',
                  },
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '0.25rem',
                }}
              >
                Event Type
              </Typography>
              <Autocomplete
                value={eventTypes.find((type) => type.value === selectedEventType) || undefined}
                onChange={(_, newValue) => {
                  setSelectedEventType(newValue?.value || 'allEvents');
                }}
                options={eventTypes}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Event Type"
                    size="small"
                    sx={{
                      '& input': {
                        fontSize: {
                          xs: '0.625rem',
                          sm: '0.8rem',
                          lg: '1rem',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: {
                          xs: '0.6rem',
                          lg: '1rem',
                        },
                      },
                    }} />
                )}
                fullWidth
                disableClearable
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                flexShrink: 0,
                marginLeft: 'auto',
                marginTop: '1.2rem'
              }}
            >
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleGenerateReport}
                disabled={loading || !selectedDevices.length}
                sx={{
                  bgcolor: getPrimaryColor() || "#1D2B56",
                  color: 'white !important',
                  padding: '0.4rem 0.8rem !important',
                  borderRadius: '0.25rem !important',
                  fontWeight: '500 !important',
                  textTransform: 'none !important',
                  fontSize: '0.7rem !important',
                  transition: 'all 0.2s ease !important',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1) !important',
                  minWidth: '75px',
                  height: '32px',
                  '&:hover': {
                    bgcolor: getPrimaryLightColor(),
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15) !important',
                  },
                }}
              >
                {loading ? <CircularProgress size={16} color="inherit" /> : 'Generate'}
              </Button>

              {allFetchedEvents.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSelectedEventType('allEvents');
                    setSelectedDevices(id ? [{ id, name: id }] : []);
                    setIsInitialMount(false); // Reset initial mount flag
                  }}
                  sx={{
                    color: '#6b7280 !important',
                    borderColor: '#6b7280 !important',
                    padding: '0.4rem 0.8rem !important',
                    borderRadius: '0.25rem !important',
                    fontWeight: '500 !important',
                    textTransform: 'none !important',
                    fontSize: '0.7rem !important',
                    transition: 'all 0.2s ease !important',
                    minWidth: '65px',
                    height: '32px',
                    borderWidth: '1px !important',
                    '&:hover': {
                      backgroundColor: '#6b7280 !important',
                      color: 'white !important',
                    },
                  }}
                >
                  Clear
                </Button>
              )}

              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportExcel}
                disabled={downloading || reportData.length === 0}
                sx={{
                  color: '#059669 !important',
                  borderColor: '#059669 !important',
                  padding: {
                    xs: '0.4rem',
                    sm: '0.4rem 0.8rem',
                  },
                  borderRadius: '0.25rem !important',
                  fontWeight: '500 !important',
                  textTransform: 'none !important',
                  fontSize: '0.7rem !important',
                  transition: 'all 0.2s ease !important',
                  minWidth: {
                    xs: '32px',
                    sm: '65px',
                  },
                  height: '32px',
                  borderWidth: '1px !important',
                  '&:hover': {
                    backgroundColor: '#059669 !important',
                    color: 'white !important',
                  },
                  '& .MuiButton-startIcon': {
                    marginRight: {
                      xs: 0,
                      sm: '0.5rem',
                    },
                    marginLeft: '0px'
                  },
                }}
              >
                {downloading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Box
                    component="span"
                    sx={{
                      display: { xs: 'none', sm: 'inline' },
                    }}
                  >
                    Excel
                  </Box>
                )}
              </Button>

              <Button
                variant="outlined"
                startIcon={<PdfIcon />}
                onClick={handleExportPDF}
                disabled={reportData.length === 0}
                sx={{
                  color: '#dc2626 !important',
                  borderColor: '#dc2626 !important',
                  padding: {
                    xs: '0.4rem',
                    sm: '0.4rem 0.8rem',
                  },
                  borderRadius: '0.25rem !important',
                  fontWeight: '500 !important',
                  textTransform: 'none !important',
                  fontSize: '0.7rem !important',
                  transition: 'all 0.2s ease !important',
                  minWidth: {
                    xs: '32px',
                    sm: '65px',
                  },
                  height: '32px',
                  borderWidth: '1px !important',
                  '&:hover': {
                    backgroundColor: '#dc2626 !important',
                    color: 'white !important',
                  },
                  '& .MuiButton-startIcon': {
                    marginRight: {
                      xs: 0,
                      sm: '0.5rem',
                    },
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: { xs: 'none', sm: 'inline' },
                  }}
                >
                  PDF
                </Box>
              </Button>

            </Box>
          </Box>
        </Box>

        {/* Table - Material-UI styled */}
        {reportData.length > 0 ? (
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead
                  style={{
                    background: 'linear-gradient(to right, #eff6ff, #e0e7ff)',
                    borderBottom: '1px solid #e5e7eb',
                  }}
                >
                  <tr>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Vehicle Number</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Type</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date & Time</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Alarm Name</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Speed</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Latitude</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Longitude</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Device Name</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Speed Limit</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Terminal ID</th>
                  </tr>
                </thead>
                <tbody style={{ backgroundColor: 'white' }}>
                  {currentData.map((row, index) => (
                    <tr
                      key={index}
                      style={{
                        borderBottom: '1px solid #f3f4f6',
                        transition: 'all 0.15s ease',
                        backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#eff6ff')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#f9fafb')}
                    >
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937' }}>
                        {row.deviceName}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            fontSize: '12px',
                            fontWeight: 600,
                            gap: '4px',
                            backgroundColor: row.type.includes('Online') ? '#dcfce7' : row.type.includes('Offline') ? '#fee2e2' : row.type.includes('alarm') ? '#fef3c7' : '#f3f4f6',
                            color: row.type.includes('Online') ? '#166534' : row.type.includes('Offline') ? '#991b1b' : row.type.includes('alarm') ? '#92400e' : '#374151',
                            border: `1px solid ${row.type.includes('Online') ? '#bbf7d0' : row.type.includes('Offline') ? '#fecaca' : row.type.includes('alarm') ? '#fde68a' : '#d1d5db'}`,
                          }}
                        >
                          {formatType(row.type)}
                        </Box>
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937' }}>
                        {formatDate(row.eventTime)}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937' }}>
                        {formatType(row.type) || "-"}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937' }}>
                        {row.alarm?.speed || "-"}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937', fontFamily: '"Monaco", "Menlo", "Ubuntu Mono", monospace' }}>
                        {row.alarm?.latitude || "-"}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937', fontFamily: '"Monaco", "Menlo", "Ubuntu Mono", monospace' }}>
                        {row.alarm?.longitude || "-"}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937' }}>
                        {row.alarm?.device_name || "-"}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937' }}>
                        {row.alarm?.speed_limit || "-"}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937' }}>
                        {row.alarm?.terminal_id || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>

            {/* Pagination */}
            <Box
              sx={{
                backgroundColor: '#f9fafb',
                padding: '1rem 1.5rem',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Typography sx={{ fontSize: '0.875rem', color: '#374151' }}>
                  Rows per page:
                </Typography>
                <select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  style={{
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    padding: '0.25rem 0.75rem',
                    fontSize: '0.875rem',
                    backgroundColor: 'white',
                    color: '#374151',
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>

                {/* Show total events count */}
                {allFetchedEvents.length > 0 && (
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '1rem' }}>
                    Total: {allFetchedEvents.length} | Filtered: {reportData.length}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <Typography sx={{ fontSize: '0.875rem', color: '#374151' }}>
                  {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, reportData.length)} of {reportData.length}
                </Typography>
                <Box sx={{ display: 'flex', gap: '0.25rem' }}>
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    style={{
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      backgroundColor: 'white',
                      color: '#374151',
                      cursor: page === 0 ? 'not-allowed' : 'pointer',
                      opacity: page === 0 ? 0.5 : 1,
                    }}
                  >
                    ←
                  </button>
                  <button
                    onClick={() => setPage(Math.min(Math.ceil(reportData.length / rowsPerPage) - 1, page + 1))}
                    disabled={page >= Math.ceil(reportData.length / rowsPerPage) - 1}
                    style={{
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      backgroundColor: 'white',
                      color: '#374151',
                      cursor: page >= Math.ceil(reportData.length / rowsPerPage) - 1 ? 'not-allowed' : 'pointer',
                      opacity: page >= Math.ceil(reportData.length / rowsPerPage) - 1 ? 0.5 : 1,
                    }}
                  >
                    →
                  </button>
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          reportFetched && (
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: '0.75rem',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                padding: '3rem',
                textAlign: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <InfoIcon color="action" />
                <Typography sx={{ fontSize: '1rem', color: '#6b7280' }}>
                  No data found
                </Typography>
              </Box>
            </Box>
          )
        )}

        {/* Download Progress Dialog */}
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={downloading}
        >
          <Box
            sx={{
              bgcolor: 'background.paper',
              p: 4,
              borderRadius: 2,
              textAlign: 'center'
            }}
          >
            <CircularProgress
              variant="determinate"
              value={downloadProgress}
              size={60}
              thickness={4}
              sx={{ mb: 2 }}
            />
            <Typography variant="h6" color="text.primary">
              {downloadProgress < 100 ? 'Preparing Download...' : 'Download Complete!'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {downloadProgress}% Complete
            </Typography>
          </Box>
        </Backdrop>
      </Box>
    </Box>
  );
};

export default EventReport;
